/**
 * NotificationPreferencesPanel — global channel toggles + per-search overrides.
 *
 * Each channel toggle PATCHes a single field (channels are independent) and the
 * cache is invalidated, so the switch reflects the persisted state. Per-search
 * overrides are listed read-only and clearly labelled as overriding the global
 * defaults.
 */
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import MenuItem from "@mui/material/MenuItem";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import {
  useNotificationPreferences,
  useUpdateGlobalPreferences,
} from "./hooks/useNotificationPreferences";
import type {
  DigestCadence,
  NotificationPreferences,
  UpdateNotificationPreferencesRequest,
} from "@records-monitor/shared";
import type { ReactElement } from "react";

const CHANNELS: readonly {
  key: keyof Pick<NotificationPreferences, "inappEnabled" | "emailEnabled" | "webhookEnabled">;
  label: string;
}[] = [
  { key: "inappEnabled", label: "In-app inbox" },
  { key: "emailEnabled", label: "Email" },
  { key: "webhookEnabled", label: "Webhooks" },
];

export function NotificationPreferencesPanel(): ReactElement {
  const { data, isLoading, isError } = useNotificationPreferences();
  const update = useUpdateGlobalPreferences();

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress aria-label="Loading preferences" />
      </Box>
    );
  }

  if (isError || !data) {
    return (
      <Alert severity="error" role="alert">
        Could not load your notification preferences.
      </Alert>
    );
  }

  const { global, perSearch } = data;

  const toggle = (patch: UpdateNotificationPreferencesRequest): void => {
    update.mutate(patch);
  };

  return (
    <Box>
      <Typography variant="h6" component="h2" gutterBottom>
        Notification preferences
      </Typography>

      <FormGroup>
        {CHANNELS.map((channel) => (
          <FormControlLabel
            key={channel.key}
            control={
              <Switch
                checked={global[channel.key]}
                onChange={(event) => toggle({ [channel.key]: event.target.checked })}
                slotProps={{ input: { "aria-label": channel.label } }}
              />
            }
            label={channel.label}
          />
        ))}
      </FormGroup>

      <TextField
        select
        label="Email digest"
        value={global.digestCadence}
        onChange={(event) => toggle({ digestCadence: event.target.value as DigestCadence })}
        size="small"
        sx={{ mt: 2, minWidth: 200 }}
      >
        <MenuItem value="off">Off (per-search emails)</MenuItem>
        <MenuItem value="daily">Daily digest</MenuItem>
        <MenuItem value="weekly">Weekly digest</MenuItem>
      </TextField>

      {update.isError ? (
        <Alert severity="error" sx={{ mt: 2 }}>
          Could not save your preference. Please try again.
        </Alert>
      ) : null}

      <Divider sx={{ my: 3 }} />

      <Typography variant="subtitle1" gutterBottom>
        Per-search overrides
      </Typography>
      {perSearch.length === 0 ? (
        <Typography color="text.secondary">
          No saved search overrides the global preferences above.
        </Typography>
      ) : (
        <Box component="ul" aria-label="Per-search overrides" sx={{ pl: 2, m: 0 }}>
          {perSearch.map((override) => (
            <Box component="li" key={override.savedSearchId} sx={{ mb: 1 }}>
              <Typography variant="body2">
                Search {override.savedSearchId} overrides defaults: in-app{" "}
                {override.preferences.inappEnabled ? "on" : "off"}, email{" "}
                {override.preferences.emailEnabled ? "on" : "off"}, webhook{" "}
                {override.preferences.webhookEnabled ? "on" : "off"}.
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}
